import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, TextField, Paper, Button, CircularProgress } from '@mui/material';
import CopyButton from './CopyButton';
import SyncIcon from '@mui/icons-material/Sync';

const ImprovementPanel = ({ issue, value, onAnswerChange }) => {
    return (
        <Paper 
            variant="outlined" 
            sx={{ 
                p: 2.5, 
                borderRadius: 2,
                borderColor: 'divider'
            }}
        >
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {issue.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {issue.promptForUser}
            </Typography>
            <TextField
                fullWidth
                multiline
                rows={3}
                variant="filled"
                label="Your answer..."
                value={value}
                onChange={(e) => onAnswerChange(issue.title, e.target.value)}
                sx={{
                    '& .MuiFilledInput-root': {
                        backgroundColor: 'action.hover',
                    },
                }}
            />
        </Paper>
    );
};

function EditablePromptSection({ issues = [], showToast, onImprovePrompt, isImproving, improvedPrompt, onReanalyze }) {
  const [answers, setAnswers] = useState({});
  const improvedPromptRef = useRef(null);
  const prevImprovedPromptRef = useRef();

  // Effect to scroll to the improved prompt when it appears
  useEffect(() => {
    const wasEmpty = !prevImprovedPromptRef.current;
    // Only scroll if the prompt was previously empty and now has content.
    if (wasEmpty && improvedPrompt) {
      setTimeout(() => {
        improvedPromptRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100); // A small delay ensures the element is rendered.
    }
    prevImprovedPromptRef.current = improvedPrompt;
  }, [improvedPrompt]);




  const handleAnswerChange = (issueTitle, answer) => {
    setAnswers(prev => ({
      ...prev,
      [issueTitle]: answer
    }));
  };

  const handleSubmit = () => {
    const formattedAnswers = issues.map(issue => ({
      question: issue.promptForUser,
      answer: answers[issue.title] || ''
    })).filter(a => a.answer.trim() !== '');

    onImprovePrompt(formattedAnswers);
  };

  const handleReanalyzeClick = () => {
    onReanalyze(improvedPrompt);
  };

  const hasAtLeastOneAnswer = Object.values(answers).some(answer => answer && answer.trim() !== '');

  return (
    <Box sx={{ mt: 4 }}>
      {/* Prim's introduction */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', mb: 3, gap: { xs: 1.5, sm: 2 } }}>
        <Box
          component="img"
          src="/prim-character.png"
          alt="Prim"
          sx={{
            height: { xs: '100px', sm: '140px' },
            width: 'auto',
            marginBottom: '-4px',
          }}
        />
        <Box
          sx={{
            flexGrow: 1,
            position: 'relative',
            bgcolor: 'primary.light',
            color: 'text.primary',
            p: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            '&::after': {
              content: '""',
              position: 'absolute',
              left: -10,
              bottom: '25px',
              width: 0,
              height: 0,
              borderTop: '10px solid transparent',
              borderBottom: '10px solid transparent',
              borderRight: '10px solid',
              borderRightColor: 'primary.light',
            },
          }}
        >
          <Typography sx={{ fontWeight: 500, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            Hello there! I'm Prim. Your prompt has some potential, darling, but it could be simply divine. If you would be so kind as to answer a few clarifying questions below, I shall personally rework it into something truly exceptional. You may, of course, skip any you deem unnecessary.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {issues.map((issue, index) => (
          <ImprovementPanel 
            key={index} 
            issue={issue} 
            value={answers[issue.title] || ''}
            onAnswerChange={handleAnswerChange}
          />
        ))}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={!hasAtLeastOneAnswer || isImproving}
            startIcon={isImproving ? <CircularProgress size={20} color="inherit" /> : null}
        >
            {isImproving ? 'Improving...' : 'Improve My Prompt'}
        </Button>
      </Box>

      {improvedPrompt && (
        <Box sx={{ mt: 4, scrollMarginTop: '24px' }} ref={improvedPromptRef}>
          <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
            Your Improved Prompt
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={10}
            value={improvedPrompt}
            InputProps={{
                readOnly: true,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.paper',
                fontSize: '1.1rem',
                fontFamily: 'typography.monospace.fontFamily'
              },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1.5 }}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<SyncIcon />}
              onClick={handleReanalyzeClick}
            >
              Re-analyze
            </Button>
            <CopyButton textToCopy={improvedPrompt} showToast={showToast} />
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default EditablePromptSection;
